<template>
  <div v-if="isLoadingRef" class="flex justify-center py-16">
    <LoadingContent :text="t('admin.setup.loading')" />
  </div>
  <p
    v-else-if="isForbiddenRef"
    role="alert"
    class="rounded-lg bg-muted p-4 text-sm leading-normal"
    data-test-id="platform-profile-forbidden"
  >
    {{ t('admin.platform.forbidden') }}
  </p>
  <p v-else-if="isErrorRef" role="alert" class="text-sm text-destructive">{{ t('admin.setup.errors.loading') }}</p>

  <div v-else class="flex flex-col gap-6" data-test-id="platform-profile">
    <header class="flex flex-wrap items-center gap-5">
      <span class="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border bg-card p-1.5">
        <img
          v-if="savedRef.LOGO_SMALL"
          :src="savedRef.LOGO_SMALL"
          alt=""
          class="h-auto! max-h-full w-auto! max-w-full object-contain!"
        />
        <span v-else class="text-xl font-bold text-muted-foreground" aria-hidden="true">{{ initialRef }}</span>
      </span>
      <div class="flex min-w-0 flex-[1_1_18.75rem] flex-col gap-1">
        <h1 class="font-display text-2xl font-semibold md:text-3xl">
          {{ savedRef.TITLE || savedRef.COMPANY_NAME }}
        </h1>
        <span class="text-sm text-muted-foreground">{{ subtitleRef }}</span>
      </div>
    </header>

    <div
      role="status"
      class="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg px-4 py-3 text-sm"
      :class="todoRef.length ? 'bg-[#fff3dc]' : 'bg-[#e7f4ee] text-[#0f5e3e]'"
      data-test-id="platform-profile-health"
    >
      <template v-if="todoRef.length">
        <TriangleAlertIcon class="size-4.5 shrink-0" aria-hidden="true" />
        <strong>{{ t('admin.platform.health.todo', { count: todoRef.length }, todoRef.length) }}</strong>
        <span class="flex flex-wrap gap-x-3 gap-y-1">
          <button
            v-for="item in todoRef"
            :key="item.key"
            type="button"
            class="font-medium text-primary underline-offset-4 hover:underline"
            @click="openSection(item.key)"
          >
            {{ t(`admin.setup.checklist.items.${item.key}`) }}
          </button>
        </span>
      </template>
      <template v-else>
        <CircleCheckIcon class="size-4.5 shrink-0" aria-hidden="true" />
        <strong>{{ t('admin.platform.health.ok') }}</strong>
      </template>
    </div>

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <section
        v-for="section in SECTIONS"
        :key="section"
        class="flex flex-col gap-2.5 rounded-lg border bg-card p-5"
        :class="{ 'border-warning': isWarning(section) }"
        :data-test-id="`platform-profile-${section}`"
      >
        <div class="flex items-center justify-between gap-3">
          <h2 class="text-base font-semibold">{{ t(`admin.platform.sections.${section}.title`) }}</h2>
          <Button
            variant="link"
            size="sm"
            class="h-auto p-0"
            @click="openSection(section)"
            :data-test-id="`platform-profile-${section}-edit`"
          >
            {{ t('admin.platform.edit') }}
            <span class="sr-only">{{ t(`admin.platform.sections.${section}.title`) }}</span>
          </Button>
        </div>

        <div v-if="section === 'branding'" class="flex flex-wrap items-center gap-2.5 text-sm">
          <img
            v-if="savedRef.LOGO_SMALL"
            :src="savedRef.LOGO_SMALL"
            alt=""
            class="size-7! rounded-sm object-contain!"
          />
          <span
            class="size-5 rounded-full border"
            :style="{ background: savedRef.COLOR_PRIMARY || 'var(--primary)' }"
            aria-hidden="true"
          />
          <span
            class="size-5 rounded-full border"
            :style="{ background: savedRef.COLOR_SECONDARY || 'var(--secondary)' }"
            aria-hidden="true"
          />
          <span class="text-muted-foreground">{{ summaries.branding }}</span>
        </div>
        <p v-else-if="section === 'resources' && savedRef.EXTERNAL_RESOURCES_URL" class="text-sm leading-relaxed">
          {{ summaries.resources }}
          <a
            :href="savedRef.EXTERNAL_RESOURCES_URL"
            target="_blank"
            rel="noopener noreferrer"
            class="break-all"
            data-test-id="platform-profile-resources-link"
          >
            {{ savedRef.EXTERNAL_RESOURCES_URL }}
          </a>
        </p>
        <p v-else class="text-sm leading-relaxed">{{ summaries[section] }}</p>

        <span
          v-if="isTodo(section)"
          class="w-fit rounded-full bg-[#fff3dc] px-2 py-0.5 text-xs font-semibold text-foreground"
        >
          {{ t('admin.setup.recap.status.todo') }}
        </span>
        <router-link v-if="section === 'legal'" :to="ROUTES.PRIVACY" class="w-fit text-sm">
          {{ t('admin.platform.sections.legal.privacyLink') }}
        </router-link>
      </section>
    </div>

    <Sheet :open="!!openSectionRef" @update:open="(open) => !open && closeSection()">
      <SheetContent
        side="right"
        :class="openSectionRef === 'branding' ? 'w-full sm:max-w-5xl' : 'w-full sm:max-w-lg'"
        class="gap-0 overflow-hidden p-0"
        data-test-id="platform-profile-sheet"
      >
        <SheetHeader class="border-b p-6">
          <SheetTitle>{{ openSectionRef ? t(`admin.platform.sections.${openSectionRef}.title`) : '' }}</SheetTitle>
          <SheetDescription>
            {{ openSectionRef ? t(`admin.platform.sections.${openSectionRef}.description`) : '' }}
          </SheetDescription>
        </SheetHeader>
        <div class="flex-1 overflow-y-auto p-6">
          <StepIdentity v-if="openSectionRef === 'identity'" />
          <StepBranding v-else-if="openSectionRef === 'branding'" with-custom-style />
          <StepLogin v-else-if="openSectionRef === 'login'" />
          <SupportFields v-else-if="openSectionRef === 'support'" :with-heading="false" />
          <ResourcesFields v-else-if="openSectionRef === 'resources'" />
          <LegalFields v-else-if="openSectionRef === 'legal'" :with-heading="false" />
          <EmailsFields v-else-if="openSectionRef === 'emails'" />
          <template v-else-if="openSectionRef === 'team'">
            <TeamFields :sso-only="savedRef.EMAIL_LOGIN_ENABLED === 'false'" />
            <p class="mt-5 rounded-md bg-muted p-3 text-sm leading-normal text-muted-foreground">
              {{ teamNoteRef }}
            </p>
          </template>
          <p v-if="errorRef" role="alert" class="mt-4 text-sm text-destructive" data-test-id="platform-profile-error">
            {{ errorRef }}
          </p>
        </div>
        <SheetFooter class="flex-row justify-end gap-3 border-t p-4">
          <Button
            v-if="openSectionRef === 'team' && canTransferRef"
            variant="ghost"
            class="mr-auto"
            data-test-id="platform-profile-transfer"
            @click="isTransferOpenRef = true"
          >
            {{ t('admin.setup.team.transfer.open') }}
          </Button>
          <Button
            v-if="openSectionRef && IMMEDIATE_SECTIONS.includes(openSectionRef)"
            variant="outline"
            data-test-id="platform-profile-close"
            @click="closeSection"
          >
            {{ t('admin.platform.close') }}
          </Button>
          <Button v-else variant="ghost" :disabled="isSavingRef" @click="closeSection">{{
            t('admin.platform.cancel')
          }}</Button>
          <Button
            v-if="!openSectionRef || !IMMEDIATE_SECTIONS.includes(openSectionRef)"
            :disabled="isSavingRef"
            data-test-id="platform-profile-save"
            @click="save"
          >
            <Spinner v-if="isSavingRef" aria-hidden="true" />
            {{ t('admin.platform.save') }}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
    <TransferOwnershipDialog :open="isTransferOpenRef" @update:open="(open) => (isTransferOpenRef = open)" />
  </div>
</template>

<script setup lang="ts">
import { CircleCheckIcon, TriangleAlertIcon } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'

import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Spinner } from '@/components/ui/spinner'
import EmailsFields from '@/features/admin/platform-setup/components/steps/emails-fields.vue'
import LegalFields from '@/features/admin/platform-setup/components/steps/legal-fields.vue'
import ResourcesFields from '@/features/admin/platform-setup/components/steps/resources-fields.vue'
import StepBranding from '@/features/admin/platform-setup/components/steps/step-branding.vue'
import StepIdentity from '@/features/admin/platform-setup/components/steps/step-identity.vue'
import StepLogin from '@/features/admin/platform-setup/components/steps/step-login.vue'
import SupportFields from '@/features/admin/platform-setup/components/steps/support-fields.vue'
import TeamFields from '@/features/admin/platform-setup/components/steps/team-fields.vue'
import TransferOwnershipDialog from '@/features/admin/platform-setup/components/transfer-ownership-dialog.vue'
import type { SaveSectionType } from '@/features/admin/platform-setup/constants'
import {
  EmailsCutNotConfirmedError,
  LoginStepError,
  providePlatformSetup,
} from '@/features/admin/platform-setup/hooks/use-platform-setup.hook'
import useSuperAdmins from '@/features/admin/platform-setup/hooks/use-super-admins.hook'
import type { ChecklistItemKeyType } from '@/features/admin/platform-setup/utils/checklist'
import { isSamlConfigured } from '@/features/admin/platform-setup/utils/checklist'
import LoadingContent from '@/features/ui/components/loading-content/loading-content.vue'
import { useI18n } from '@/lib/i18n'
import { ROUTES } from '@/utils/config/constants'
import { textContrastRatio } from '@/utils/misc/contrast'

// Les sections de la fiche reprennent les points de la checklist, et leurs formulaires
// sont ceux de l'assistant de mise en route. Le lien des ressources, facultatif, a sa propre
// section : rangé sous « Support », personne ne le trouvait (KC-654).
type SectionType = ChecklistItemKeyType | 'resources' | 'emails' | 'team'
const SECTIONS: SectionType[] = ['identity', 'branding', 'login', 'support', 'resources', 'emails', 'team', 'legal']
// L'équipe agit tout de suite (invitation, retrait) : son panneau n'a rien à enregistrer.
const IMMEDIATE_SECTIONS: SectionType[] = ['team']
const SAVED_STEP: Record<SectionType, SaveSectionType> = {
  identity: 'identity',
  branding: 'branding',
  login: 'login',
  support: 'support',
  resources: 'resources',
  legal: 'legal',
  emails: 'emails',
  team: 'team',
}

const { t, locale } = useI18n()
const { savedRef, checklistRef, isLoadingRef, isErrorRef, isForbiddenRef, isSavingRef, saveStep, resetForm } =
  providePlatformSetup()

const { superAdminsRef, ownerRef, canTransferRef } = useSuperAdmins()
const isTransferOpenRef = ref(false)

// Le propriétaire lit la règle générale ; les autres super admins savent à qui s'adresser.
const teamNoteRef = computed(() => {
  const owner = ownerRef.value
  if (!owner || owner.isCurrentUser) return t('admin.platform.sections.team.note')
  const name = [owner.firstname, owner.lastname].filter(Boolean).join(' ') || owner.email
  return t('admin.platform.sections.team.noteMember', { name })
})

const todoRef = computed(() => checklistRef.value.filter((item) => !item.done))
const isTodo = (section: SectionType) => todoRef.value.some((item) => item.key === section)
// Les e-mails coupés ne sont pas « à compléter », mais méritent d'être signalés.
const isWarning = (section: SectionType) =>
  isTodo(section) || (section === 'emails' && savedRef.value.ENABLE_TRANSACTIONAL_EMAILS !== 'true')

const initialRef = computed(() => (savedRef.value.COMPANY_NAME || savedRef.value.TITLE || '?').trim().charAt(0))
const subtitleRef = computed(() => [window.location.host, savedRef.value.COMPANY_NAME].filter(Boolean).join(' · '))

const summaries = computed<Record<SectionType, string>>(() => {
  const s = savedRef.value
  const ratio = s.COLOR_PRIMARY ? textContrastRatio(s.COLOR_PRIMARY) : null
  const ratioText =
    ratio === null
      ? ''
      : new Intl.NumberFormat(locale.value, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(ratio)
  const email = s.EMAIL_LOGIN_ENABLED === 'true'
  const sso = isSamlConfigured(s)
  const loginKey = email && sso ? 'both' : sso ? 'sso' : email ? 'email' : 'none'
  return {
    identity:
      s.TITLE && s.COMPANY_NAME
        ? t('admin.platform.sections.identity.summary', { title: s.TITLE, company: s.COMPANY_NAME })
        : t('admin.platform.sections.identity.missing'),
    branding: ratioText
      ? t('admin.platform.sections.branding.summary', { ratio: ratioText })
      : t('admin.platform.sections.branding.missing'),
    login: t(`admin.platform.sections.login.summary.${loginKey}`),
    support: s.SUPPORT_EMAIL
      ? s.SUPPORT_PHONE
        ? t('admin.platform.sections.support.summaryWithPhone', { email: s.SUPPORT_EMAIL, phone: s.SUPPORT_PHONE })
        : t('admin.platform.sections.support.summary', { email: s.SUPPORT_EMAIL })
      : t('admin.platform.sections.support.missing'),
    resources: s.EXTERNAL_RESOURCES_URL
      ? t('admin.platform.sections.resources.summary')
      : t('admin.platform.sections.resources.missing'),
    emails:
      s.ENABLE_TRANSACTIONAL_EMAILS === 'true'
        ? t('admin.platform.sections.emails.summary.on')
        : t('admin.platform.sections.emails.summary.off'),
    team: teamSummary(),
    legal:
      s.LEGAL_RT_ORGANISME_NAME && s.LEGAL_RT_DPO_EMAIL
        ? t('admin.platform.sections.legal.summary', { company: s.LEGAL_RT_ORGANISME_NAME, dpo: s.LEGAL_RT_DPO_EMAIL })
        : t('admin.platform.sections.legal.missing'),
  }
})

function teamSummary() {
  const active = superAdminsRef.value.filter((admin) => !admin.pending).length
  const pending = superAdminsRef.value.length - active
  if (!superAdminsRef.value.length) return t('admin.platform.sections.team.missing')
  const parts = [
    active ? t('admin.platform.sections.team.summary.active', { count: active }, active) : '',
    pending ? t('admin.platform.sections.team.summary.pending', { count: pending }, pending) : '',
  ]
  return parts.filter(Boolean).join(' ')
}

const openSectionRef = ref<SectionType | null>(null)
const errorRef = ref('')

function openSection(section: SectionType) {
  // Chaque ouverture repart des valeurs enregistrées : un brouillon abandonné ne revient pas.
  resetForm()
  errorRef.value = ''
  openSectionRef.value = section
}

function closeSection() {
  openSectionRef.value = null
  resetForm()
}

async function save() {
  if (!openSectionRef.value) return
  errorRef.value = ''
  try {
    await saveStep(SAVED_STEP[openSectionRef.value])
    toast.success(t('admin.platform.saved'))
    openSectionRef.value = null
  } catch (error) {
    errorRef.value =
      error instanceof LoginStepError || error instanceof EmailsCutNotConfirmedError
        ? t(error.message)
        : t('admin.setup.errors.save')
  }
}
</script>
